import { Link } from "wouter";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { assets, featuredProjects, professionalEvidence, recognition } from "@/content";
import { ArrowLink, PageMeta } from "@/components/SiteLayout";

function ProjectCard({ project, index }: { project: (typeof featuredProjects)[number]; index: number }) {
  return (
    <Link href={`/work/${project.slug}`} className={`project-card project-card--${project.tone} ${index === 0 ? "project-card--wide" : ""}`}>
      {project.image ? (
        <img src={project.image} alt={project.imageAlt} loading={index === 0 ? "eager" : "lazy"} />
      ) : (
        <div className="project-card-field" aria-hidden="true"><span>{project.number}</span><i></i><i></i></div>
      )}
      <div className="project-card-overlay"></div>
      <div className="project-card-meta"><span>{project.year}</span><span>{project.format.split("·")[0]}</span></div>
      <div className="project-card-copy">
        <p>{project.number}</p><h3>{project.shortTitle}</h3><p className="project-question">{project.question}</p>
        <span className="card-arrow" aria-hidden="true"><ArrowUpRight size={18} /></span>
      </div>
    </Link>
  );
}

export default function Home() {
  return (
    <>
      <PageMeta
        title="Pawan Sharma — Filmmaker, Researcher & Visual Media Artist"
        description="Pawan Sharma works across documentary film, media research, public communication, and visual art in India and the United States."
        path="/"
      />

      <section className="hero hero--portrait">
        <figure className="hero-portrait-frame">
          <img className="hero-portrait" src={assets.portrait} alt="Black-and-white portrait of Pawan Sharma" />
          <div className="hero-focus" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
          <figcaption><span>PORTRAIT / 2023</span><span>MINNEAPOLIS–SAINT PAUL</span></figcaption>
        </figure>
        <div className="hero-grid" aria-hidden="true"><span></span><span></span><span></span></div>
        <div className="hero-content hero-content--portrait">
          <p className="hero-kicker">Documentary filmmaker · researcher · visual media artist</p>
          <h1>I follow how<br />stories <em>move.</em></h1>
          <div className="hero-bottom">
            <p>I grew up in Malwa, central India, and now live in Minnesota. My work follows images through theaters, platforms, exhibitions, public institutions, and immigrant life—and asks what happens to people after a story begins to circulate.</p>
            <div className="hero-actions">
              <a href="#selected-work" className="button button--light">View selected work <ArrowDown size={16} /></a>
              <Link href="/about" className="quiet-link">About me</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="manifesto paper-section">
        <div className="section-index">MALWA → MINNESOTA<br />ONE PRACTICE</div>
        <div className="manifesto-copy reveal">
          <p className="eyebrow">Where the work begins</p>
          <h2>An image belongs to a place,<br />a language, and <em>a community.</em></h2>
          <div className="two-col-text">
            <p>I entered media studies through making films in Malwa. Local theaters, traveling screenings, pirated DVDs, wedding filmmakers, and shared acts of watching formed the media world around me. Working there made filmmaking an embodied and collective practice.</p>
            <p>After migrating to the United States, I became more attentive to how people read public space, carry memory across borders, and negotiate visibility. That movement shapes how I frame a face, record a voice, study a platform, and write for a public audience.</p>
          </div>
          <ArrowLink href="/practice">How I work</ArrowLink>
        </div>
      </section>

      <section id="selected-work" className="selected-work ink-section">
        <div className="section-heading section-heading--light reveal">
          <p className="eyebrow light">Selected work · 2014—ongoing</p>
          <h2>Research, films,<br /><em>and public projects.</em></h2>
          <p>Each project begins with a concrete place or problem. The form—fieldwork, film, photography, installation, writing, or public communication—follows from what the work requires.</p>
        </div>
        <div className="project-grid reveal">
          {featuredProjects.map((project, index) => <ProjectCard key={project.slug} project={project} index={index} />)}
        </div>
        <div className="section-end-link"><ArrowLink href="/films-art" className="light-link">See all films and visual work</ArrowLink></div>
      </section>

      <section className="archive-sequence paper-section" aria-label="Selected visual archive">
        <figure className="archive-frame archive-frame--large reveal">
          <img src={assets.movingFlowInstallation} alt="A Moving Tracts installation with lentils and peas flowing onto the gallery floor" loading="lazy" />
          <figcaption><span>01</span> Moving Tracts · installation view · 2021</figcaption>
        </figure>
        <figure className="archive-frame archive-frame--small reveal">
          <img src={assets.movingCooking} alt="A hand stirring food in a metal pot during the Moving Tracts project" loading="lazy" />
          <figcaption><span>02</span> Moving Tracts · food narrative process</figcaption>
        </figure>
        <figure className="archive-frame archive-frame--tall reveal">
          <img src={assets.stormBranches} alt="Dark winter branches crossing a white Minnesota sky" loading="lazy" />
          <figcaption><span>03</span> Storm, Minnesota · photographic archive</figcaption>
        </figure>
      </section>

      <section className="professional-practice ink-section">
        <div className="professional-intro reveal">
          <p className="eyebrow light">Research that enters public work</p>
          <h2>I move from close research<br />to <em>clear public use.</em></h2>
          <p>Academic research gives me time to study media systems in depth. Work in film production, municipal policy, nonprofit communication, campaigns, and independent publishing has taught me to turn that research into decisions: how to frame a story, understand an audience, organize a production, explain a policy, or build a program with partners.</p>
          <ArrowLink href="/practice" className="light-link">See professional and public work</ArrowLink>
        </div>
        <div className="professional-capabilities reveal">
          {[
            ["Research audiences and media use", "Multilingual interviews, digital ethnography, platform walkthroughs, qualitative coding, audience mapping, and policy research."],
            ["Write, edit, and publish", "Long-form writing, scripts, public information, visual selection, podcast production, web publishing, and audience engagement."],
            ["Lead programs and partnerships", "Budgets, grants, workplans, partner review, workshops, exhibitions, screenings, and cross-institutional delivery."],
            ["Make films and visual stories", "Research, concept development, cinematography, sound, editing, exhibition design, and platform-specific media."],
          ].map(([title, text], index) => <article key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{text}</p></article>)}
        </div>
        <div className="evidence-grid reveal">
          {professionalEvidence.map((item) => <article key={item.label}><p>{item.value}</p><h3>{item.label}</h3><span>{item.detail}</span></article>)}
        </div>
      </section>

      <section className="research-feature indigo-section">
        <div className="research-coordinate" aria-hidden="true">22.7196° N<br />75.8577° E</div>
        <div className="research-feature-copy reveal">
          <p className="eyebrow light">Current research · Malwa, central India</p>
          <h2>Infrastructures<br />of <em>Vision</em></h2>
          <p className="lead">I study technological change where it becomes ordinary work.</p>
          <p>The dissertation follows a projectionist trying to keep an aging theater open, creators making regional language and humor legible to recommendation systems, and small studios combining wedding films, political content, music, and promotion to remain viable.</p>
          <div className="research-tags">
            {['Creators and audiences', 'Platform design', 'Media production', 'Policy and access', 'AI-assisted work'].map(tag => <span key={tag}>{tag}</span>)}
          </div>
          <ArrowLink href="/research" className="light-link">Read about the research</ArrowLink>
        </div>
        <figure className="research-photo reveal">
          <img src={assets.stormPineCity} alt="A pine branch in focus with a blurred Minnesota street behind it" loading="lazy" />
          <figcaption>Observation, place, public life · Minnesota</figcaption>
        </figure>
      </section>

      <section className="public-work paper-section">
        <div className="public-image reveal">
          <img src={assets.soundStories} alt="A hand operating an interactive audiovisual interface for Sound Stories" loading="lazy" />
          <span>Sound Stories · interactive audiovisual production · 2022</span>
        </div>
        <div className="public-copy reveal">
          <p className="eyebrow">Public communication</p>
          <h2>Clear communication begins with the questions people are asking.</h2>
          <p>At SEWA-AIFW, I produced short videos, infographics, captions, and accessible materials for immigrant communities. At Smart City Corporation, I led policy and public-information work across digital access, transportation, and urban services. Through The Elsewhere Lab, I manage research, writing, recording, editing, visual selection, web publishing, and audience engagement.</p>
          <blockquote>“I am comfortable moving between communications strategy and the detailed work of a sentence, image, cut, caption, or production schedule.”</blockquote>
          <ArrowLink href="/practice">Read the professional record</ArrowLink>
        </div>
      </section>

      <section className="teaching-teaser rust-section">
        <div className="teaching-number" aria-hidden="true">T<br />04</div>
        <div className="teaching-teaser-copy reveal">
          <p className="eyebrow light">Teaching</p>
          <h2>Critical inquiry with<br /><em>media production.</em></h2>
          <p>My courses move from visual grammar and mobile photography into platform storytelling, memes, post-truth media, digital archives, and AI. Students make work, explain their choices, and learn to connect a creative decision to a larger question about culture and power.</p>
          <ArrowLink href="/teaching" className="light-link">Teaching and courses</ArrowLink>
        </div>
      </section>

      <section className="recognition paper-section">
        <div className="section-heading reveal"><p className="eyebrow">Selected record</p><h2>Grants, fellowships,<br />and exhibitions.</h2></div>
        <div className="recognition-list reveal">{recognition.map(([year, title]) => <div key={title}><span>{year}</span><p>{title}</p></div>)}</div>
        <ArrowLink href="/cv">View the academic CV</ArrowLink>
      </section>
    </>
  );
}
